import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';
import toast from 'react-hot-toast';
import firebaseErrorMessage from '../../../utils/firebaseErrorMessage';
import {
  EXTRA_SEAT_PRICE_USD_PROFESSIONAL,
  EXTRA_SEAT_PRICE_USD_STUDIO,
  INCLUDED_SEATS_PROFESSIONAL,
  INCLUDED_SEATS_STUDIO,
} from '../../../constants/seatPricing';
import { normalizeSubscriptionPlan } from '../../../constants/uploadLimits';
import { normalizeProjectMemberRole } from '../../../constants/projectMemberRoles';
import { editorsFromPlanAndLegacy } from '../../../utils/billingSeatsFromPlan';
import './SeatManagerSection.css';

function memberLabel(m) {
  if (m.displayName && m.displayName.trim()) return m.displayName.trim();
  if (m.email && m.email.trim()) return m.email.trim();
  return m.id;
}

function projectIdsFromBillingSeat(m) {
  const map = m.projects && typeof m.projects === 'object' ? m.projects : {};
  return Object.keys(map).filter((pid) => map[pid] === true);
}

function formatProjectNames(projectIds, projectNameById) {
  if (!projectIds.length) return '—';
  return projectIds
    .map((pid) => projectNameById[pid] || pid.slice(0, 8))
    .join(', ');
}

function projectNamesForMember(projectIds, projectNameById) {
  return formatProjectNames(projectIds, projectNameById);
}

/** Scan studio project docs for editor/viewer membership (source of truth for project lists). */
function buildStudioMembership(studioDocs, ownerUid) {
  const projectNameById = {};
  const editorProjectIdsByUid = {};
  const viewersByUid = new Map();

  for (const p of studioDocs) {
    const data = p.data() || {};
    if (data._placeholder) continue;
    const projectId = p.id;
    projectNameById[projectId] = String(data.projectName || projectId).trim() || projectId;

    const memberIds = Array.isArray(data.memberIds) ? data.memberIds : [];
    const memberRoles =
      data.memberRoles && typeof data.memberRoles === 'object' ? data.memberRoles : {};
    const memberProfiles =
      data.memberProfiles && typeof data.memberProfiles === 'object' ? data.memberProfiles : {};

    for (const memberUid of memberIds) {
      if (!memberUid || memberUid === ownerUid) continue;
      const role = normalizeProjectMemberRole(memberRoles[memberUid]);
      const profile =
        memberProfiles[memberUid] && typeof memberProfiles[memberUid] === 'object'
          ? memberProfiles[memberUid]
          : {};

      if (role === 'viewer') {
        const prev = viewersByUid.get(memberUid) || {
          id: memberUid,
          email: String(profile.email || '').trim(),
          displayName: String(profile.displayName || '').trim(),
          projectIds: [],
        };
        if (!prev.email && profile.email) prev.email = String(profile.email).trim();
        if (!prev.displayName && profile.displayName) {
          prev.displayName = String(profile.displayName).trim();
        }
        if (!prev.projectIds.includes(projectId)) prev.projectIds.push(projectId);
        viewersByUid.set(memberUid, prev);
      } else {
        if (!editorProjectIdsByUid[memberUid]) editorProjectIdsByUid[memberUid] = [];
        if (!editorProjectIdsByUid[memberUid].includes(projectId)) {
          editorProjectIdsByUid[memberUid].push(projectId);
        }
      }
    }
  }

  return {
    projectNameById,
    editorProjectIdsByUid,
    viewers: Array.from(viewersByUid.values()),
  };
}

function mergedEditorProjectIds(editor, editorProjectIdsByUid) {
  const ids = new Set([
    ...projectIdsFromBillingSeat(editor),
    ...(editorProjectIdsByUid[editor.id] || []),
  ]);
  return Array.from(ids);
}

/**
 * Editor seat usage + flat editor list (Figma-style per-project sharing).
 */
export default function SeatManagerSection({
  ownerUid,
  currentUserUid,
  ownerEmail,
  billingPlanKey,
  currentAddonQty = 0,
  seatCap = null,
}) {
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [editorProjectIdsByUid, setEditorProjectIdsByUid] = useState({});
  const [pendingEditorInvites, setPendingEditorInvites] = useState(0);
  const [projectNameById, setProjectNameById] = useState({});
  const [addonBusy, setAddonBusy] = useState(false);

  const plan = normalizeSubscriptionPlan(billingPlanKey);
  const seatAddonUsd =
    plan === 'studio' ? EXTRA_SEAT_PRICE_USD_STUDIO : EXTRA_SEAT_PRICE_USD_PROFESSIONAL;
  const includedSeats =
    plan === 'studio' ? INCLUDED_SEATS_STUDIO : INCLUDED_SEATS_PROFESSIONAL;

  const refreshLists = useCallback(async () => {
    if (!ownerUid) return;
    try {
      const [editorSnap, settingsSnap, studioSnap] = await Promise.all([
        getDocs(collection(db, 'users', ownerUid, 'billingSeats')),
        getDoc(doc(db, 'users', ownerUid, 'billing', 'plan')),
        getDocs(collection(db, 'users', ownerUid, 'studio')),
      ]);
      const editorRows = editorsFromPlanAndLegacy(settingsSnap.data(), editorSnap.docs);
      setEditors(editorRows);
      setPendingEditorInvites(Number(settingsSnap.data()?.seats?.pendingEditorInvites ?? 0) || 0);
      const membership = buildStudioMembership(studioSnap.docs, ownerUid);
      setProjectNameById(membership.projectNameById);
      setEditorProjectIdsByUid(membership.editorProjectIdsByUid);
      const editorUidSet = new Set(editorRows.map((d) => d.id));
      setViewers(membership.viewers.filter((v) => !editorUidSet.has(v.id)));
    } catch (_) {
      setEditors([]);
      setViewers([]);
      setEditorProjectIdsByUid({});
      setPendingEditorInvites(0);
      setProjectNameById({});
    }
  }, [ownerUid]);

  useEffect(() => {
    void refreshLists();
  }, [refreshLists]);

  const resolvedCap = useMemo(() => {
    if (seatCap != null && Number.isFinite(Number(seatCap))) return Number(seatCap);
    return includedSeats + (Number(currentAddonQty) || 0);
  }, [seatCap, includedSeats, currentAddonQty]);

  const editorCount = editors.length;
  const totalEditorSeatsUsed = 1 + editorCount + pendingEditorInvites;
  const isEnterprise = plan === 'enterprise';
  const atSeatCap = !isEnterprise && totalEditorSeatsUsed >= resolvedCap;

  const changeAddon = async (delta) => {
    const next = Math.min(500, Math.max(0, (Number(currentAddonQty) || 0) + delta));
    setAddonBusy(true);
    try {
      const fn = httpsCallable(functions, 'syncBillingFromStripe');
      await fn({ extraSeatAddonQuantity: next });
      toast.success(next === 0 ? 'Removed extra seats' : 'Seat add-on quantity updated.');
      window.dispatchEvent(new Event('starling:refresh-user-plan'));
      await refreshLists();
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Could not update seats in Stripe'));
    } finally {
      setAddonBusy(false);
    }
  };

  if (plan !== 'professional' && plan !== 'studio' && plan !== 'enterprise') return null;

  return (
    <section className="seat-manager">
      <h2 className="seat-manager-title">Team seats overview</h2>
      <p className="seat-manager-desc">
        {plan === 'enterprise'
          ? 'Unlimited editor seats on Enterprise. Viewers are always free.'
          : `Editors consume paid seats across all your projects. Viewers are free and unlimited.`}
        {plan !== 'enterprise' && (
          <>
            {' '}
            Extra seats billed at ${seatAddonUsd}/seat/month via Stripe.
          </>
        )}
      </p>

      <div className="seat-manager-counter" role="status">
        EDITOR SEATS USED: {totalEditorSeatsUsed} / {isEnterprise ? 'UNLIMITED' : resolvedCap}
      </div>

      {atSeatCap && plan !== 'enterprise' ? (
        <p className="seat-manager-cap-hint">
          Editor seat limit reached. Add extra seats below or revoke pending editor invites from project team panels.
        </p>
      ) : null}

      {plan !== 'enterprise' ? (
        <div className="seat-manager-addon">
          <span className="seat-manager-addon-label">
            Extra seat add-ons: {Number(currentAddonQty) || 0}
          </span>
          <button
            type="button"
            className="seat-manager-addon-btn"
            disabled={addonBusy || currentAddonQty <= 0}
            onClick={() => changeAddon(-1)}
            aria-label="Remove one extra seat"
          >
            −
          </button>
          <button
            type="button"
            className="seat-manager-addon-btn"
            disabled={addonBusy}
            onClick={() => changeAddon(1)}
            aria-label="Add one extra seat"
          >
            +
          </button>
        </div>
      ) : null}

      <div className="seat-manager-block">
        <div className="seat-manager-block-title">Owner</div>
        <div className="seat-manager-card">
          <div className="seat-manager-card-main">
            <span className="seat-manager-card-email">{ownerEmail || '—'}</span>
            {currentUserUid && currentUserUid === ownerUid ? (
              <span className="seat-manager-card-tag">YOU</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="seat-manager-block">
        <div className="seat-manager-block-title">Editor seats</div>
        {!editors.length ? (
          <div className="seat-manager-empty">None yet. Invite editors from each project&apos;s team panel.</div>
        ) : (
          <ul className="seat-manager-list">
            {editors.map((m) => (
              <li key={m.id} className="seat-manager-card seat-manager-card--row">
                <div className="seat-manager-row-main">
                  <span className="seat-manager-card-email">{m.email?.trim() || memberLabel(m)}</span>
                  <span className="seat-manager-row-role">
                    Projects:{' '}
                    {projectNamesForMember(
                      mergedEditorProjectIds(m, editorProjectIdsByUid),
                      projectNameById,
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="seat-manager-block">
        <div className="seat-manager-block-title">Viewers</div>
        {!viewers.length ? (
          <div className="seat-manager-empty">
            None yet. Invite viewers from each project&apos;s team panel. Viewers are free and
            unlimited.
          </div>
        ) : (
          <ul className="seat-manager-list">
            {viewers.map((m) => (
              <li key={m.id} className="seat-manager-card seat-manager-card--row">
                <div className="seat-manager-row-main">
                  <span className="seat-manager-card-email">
                    {m.email?.trim() || memberLabel(m)}
                  </span>
                  <span className="seat-manager-row-role">
                    Projects: {projectNamesForMember(m.projectIds || [], projectNameById)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pendingEditorInvites > 0 ? (
        <div className="seat-manager-block">
          <div className="seat-manager-block-title">Pending editor invites</div>
          <div className="seat-manager-empty">{pendingEditorInvites} pending (across projects)</div>
        </div>
      ) : null}
    </section>
  );
}
